import type { HttpClient } from '../http.js';
import type { Customer, Order, Subscription } from '../types.js';

/**
 * Customer data -- profile, orders, subscriptions.
 *
 * Requires either an API key or a session token for authentication.
 */
export class CustomerModule {
  constructor(
    private readonly http: HttpClient,
    private readonly storeId: string,
  ) {}

  /** Get customer profile by ID. */
  async getProfile(customerId: string): Promise<Customer> {
    const res = await this.http.get<{ customer: Customer }>(
      `/api/v1/customers/${customerId}`,
      { params: { storeId: this.storeId } },
    );
    return res.customer;
  }

  /** Get customer profile by email. */
  async getByEmail(email: string): Promise<Customer | null> {
    try {
      const res = await this.http.get<{ customer: Customer }>(
        '/api/v1/customers/by-email',
        { params: { email, storeId: this.storeId } },
      );
      return res.customer;
    } catch {
      return null;
    }
  }

  /** Update customer profile fields. */
  async updateProfile(
    customerId: string,
    data: Partial<Pick<Customer, 'email' | 'firstName' | 'lastName' | 'phone' | 'metadata'>>,
  ): Promise<Customer> {
    const res = await this.http.patch<{ customer: Customer }>(
      `/api/v1/customers/${customerId}`,
      { storeId: this.storeId, ...data },
    );
    return res.customer;
  }

  /** List orders for a customer. */
  async getOrders(
    customerId: string,
    opts?: { limit?: number; offset?: number },
  ): Promise<{ orders: Order[]; total: number }> {
    return this.http.get<{ orders: Order[]; total: number }>(
      `/api/v1/customers/${customerId}/orders`,
      { params: { storeId: this.storeId, limit: opts?.limit, offset: opts?.offset } },
    );
  }

  /**
   * Get a single order by ID.
   *
   * **Thank-you pages:** accepted one-click upsells create *child orders*
   * (`metadata.mainOrderId` = this order's id) that land asynchronously —
   * the first fetch right after an accept usually misses them. Pass
   * `waitForRelatedOrders: true` to poll until `order.relatedOrders` is
   * non-empty or the timeout elapses. On timeout the latest snapshot is
   * returned (never throws), so an order with no accepted upsell simply
   * comes back with an empty `relatedOrders` after `timeoutMs`.
   *
   * @example
   * ```ts
   * // Thank-you page: show the main order AND the upsells
   * const order = await tagada.customer.getOrder(orderId, {
   *   waitForRelatedOrders: true,
   * });
   * renderOrders([order, ...(order.relatedOrders ?? [])]);
   * ```
   */
  async getOrder(
    orderId: string,
    opts?: {
      /** Poll until `relatedOrders` is non-empty (or `timeoutMs` elapses). */
      waitForRelatedOrders?: boolean;
      /** Max total wait in ms. Default: 8000. */
      timeoutMs?: number;
      /** Delay between polls in ms. Default: 2000. */
      intervalMs?: number;
    },
  ): Promise<Order> {
    const fetchOrder = async (): Promise<Order> => {
      const res = await this.http.get<{ order: Order }>(`/api/v1/orders/${orderId}`, {
        params: { storeId: this.storeId },
      });
      return res.order;
    };

    let order = await fetchOrder();
    if (!opts?.waitForRelatedOrders || order.relatedOrders?.length) return order;

    const timeoutMs = opts.timeoutMs ?? 8000;
    const intervalMs = opts.intervalMs ?? 2000;
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, Math.min(intervalMs, Math.max(0, deadline - Date.now()))));
      order = await fetchOrder();
      if (order.relatedOrders?.length) return order;
    }
    return order;
  }

  /** List subscriptions for a customer. */
  async getSubscriptions(customerId: string): Promise<Subscription[]> {
    const res = await this.http.get<{ subscriptions: Subscription[] }>(
      `/api/v1/customers/${customerId}/subscriptions`,
      { params: { storeId: this.storeId } },
    );
    return res.subscriptions;
  }

  /** Get a single subscription by ID. */
  async getSubscription(subscriptionId: string): Promise<Subscription> {
    const res = await this.http.get<{ subscription: Subscription }>(
      `/api/v1/subscriptions/${subscriptionId}`,
      { params: { storeId: this.storeId } },
    );
    return res.subscription;
  }
}
